import React, { useState } from 'react'
import { FormLayout } from 'styles/FormLayout'
import {
  ActionNames,
  Config,
  countAffectedRows,
  DisableAllActionType,
  DisabledActionType,
  isDisableAllAction,
  isDisabledAction,
  isRefundGroupAction,
  isReturnAction,
  isSuperGroupAction,
  ReturnActionType,
  saveActions,
  ShapeAction,
  stagedIfChanged,
  withoutUndefinedActions,
} from 'domain/Assortment'
import { Dialog } from '../../Dialog'
import { StagedModifications } from '../StagedModifications'
import { DisabledShapeActions, ShapeActionDialog, ShapeActionSnapshot } from './ShapeActionDialog'
import { Action, EditableActions, FinalStatus, StagedAction } from '../CommonComponents'
import { BottleGroup, Modification, ShapeFile } from 'domain/DTO'
import { SortableHeader, useTableSorting } from '../TableColumnSort'
import { paginationOptions } from '../AssortmentDatabase'
import { getPageData, PagedTableControls } from '@tomra/react-table'
import { logError } from '../../../lib/logError'
import { hasWriteCommonAssortmentPermission, hasWriteInstallationPermission } from '../../../lib/Permission'
import {
  DisabledShapeActionsMultiselect,
  ShapeActionDialogMultiselect,
  ShapeActionSnapshotMultiselect,
} from './ShapeActionDialogMulti'
import Colors from '../../../styles/colors'

const ReturnValue = ({ value }: { value: number }) => <div>{value === 0 ? 'Accepted' : 'Returned'}</div>
const RefundGroupValue = ({ value }: { value: number }) => <div>{`Refund group ${value}`}</div>
const SuperGroupValue = ({ value }: { value: number }) => <div>{`Super group ${value}`}</div>

const MULTI_ID = '__MULTI__'

type DialogState = {
  show: boolean
  id: string
  ids?: string[]
  snapshot: ShapeActionSnapshot
  disabled: DisabledShapeActions
}

type Props = {
  config: Config
  database: ShapeFile
  commonModifications: Modification[]
  installationModifications: Modification[]
  modificationUrl: string
  refreshModifications(): void
}

export const ShapeInstallationTable = ({
  config,
  database,
  commonModifications,
  installationModifications,
  modificationUrl,
  refreshModifications,
}: Props) => {
  const [dialogState, setDialogState] = useState({ show: false } as DialogState)
  const [currentPage, setCurrentPage] = useState(1)
  const [recordsPrPage, setRecordsPrPage] = useState(100)
  const [filterGroup, setFilterGroup] = useState('')
  const [filterName, setFilterName] = useState('')
  const [staged, setStaged] = useState<{ [key: string]: ShapeAction | undefined }>({})
  const [showDetailed, setShowDetailed] = useState(false)
  const common = createShapeActions(commonModifications)
  const override = createShapeActions(installationModifications)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [multiSnapshot, setMultiSnapshot] = useState<ShapeActionSnapshotMultiselect | null>(null)
  const [showMultiCheckboxes, setShowMultiCheckboxes] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmLines, setConfirmLines] = useState<string[]>([])

  const sorting = useTableSorting<BottleGroup>(
    {
      'Bottle Group': (bg) => bg.groupNumber,
      Name: (bg) => bg.name,
      CommonConfig: (bg) => {
        const id = bg.groupNumber
        return (
          [returnActionId(id), disabledActionId(id), refundGroupActionId(id), superGroupActionId(id)]
            .map((id) => staged[id])
            .filter((action) => !!action)[0]
            ?.type.toString() ?? ''
        )
      },
      Override: (bg) => {
        const id = bg.groupNumber
        return (
          [returnActionId(id), disabledActionId(id), refundGroupActionId(id), superGroupActionId(id)]
            .map((id) => override?.[id])
            .filter((action) => !!action)[0]
            ?.type.toString() ?? ''
        )
      },
      Accepted: (bg) => {
        return calculateFinalStatus(bg, common, override, staged).toString()
      },
    },
    'Bottle Group',
  )

  const rows = database?.bottleGroups
    ? sorting.sort(
        database.bottleGroups
          .filter((bg) =>
            `${bg.groupNumber}-bottle ${bg.groupNumber}-super ${bg.superGroupNumber}-refund ${bg.refundGroup}`
              .toLocaleLowerCase()
              .includes(filterGroup),
          )
          .filter((bg) => `${bg.name}`.toLocaleLowerCase().includes(filterName)),
      )
    : []

  function canOverride(actionId: string) {
    const allow = common[actionId]?.allowOverride
    return typeof allow === 'undefined' ? true : allow
  }

  function stageMultiForId(id: string) {
    if (!multiSnapshot) return
    setStaged((prev) => {
      const next = { ...prev }
      if (multiSnapshot.returnAction && canOverride(returnActionId(id))) {
        if (multiSnapshot.returnAction.type === ReturnActionType.REMOVE) {
          next[returnActionId(id)] = stagedIfChanged(
            { ...multiSnapshot.returnAction, type: 'UNSET' as ReturnActionType },
            override[returnActionId(id)],
          )
        } else {
          next[returnActionId(id)] = stagedIfChanged(multiSnapshot.returnAction, override[returnActionId(id)])
        }
      }
      if (multiSnapshot.disabledAction && canOverride(disabledActionId(id))) {
        if (multiSnapshot.disabledAction.type === DisabledActionType.REMOVE) {
          next[disabledActionId(id)] = stagedIfChanged(
            { ...multiSnapshot.disabledAction, type: 'UNSET' as DisabledActionType },
            override[disabledActionId(id)],
          )
        } else {
          next[disabledActionId(id)] = stagedIfChanged(multiSnapshot.disabledAction, override[disabledActionId(id)])
        }
      }
      return next
    })
  }

  function clearMultiForId(id: string) {
    if (!multiSnapshot) return
    setStaged((prev) => {
      const next = { ...prev }
      if (multiSnapshot.returnAction && canOverride(returnActionId(id))) delete next[returnActionId(id)]
      if (multiSnapshot.disabledAction && canOverride(disabledActionId(id))) delete next[disabledActionId(id)]
      return next
    })
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      const willSelect = !next.has(id)
      if (willSelect) {
        next.add(id)
        stageMultiForId(id)
      } else {
        next.delete(id)
        clearMultiForId(id)
      }
      return next
    })
  }

  function clearSelection() {
    setSelectedIds(new Set())
  }

  function selectAllIds(checked: boolean) {
    if (!showMultiCheckboxes) return
    if (checked) {
      const allIds = rows.map((bg) => bg.groupNumber)
      setSelectedIds(new Set(allIds))
      allIds.forEach(stageMultiForId)
    } else {
      Array.from(selectedIds).forEach(clearMultiForId)
      clearSelection()
    }
  }

  function resetMulti() {
    if (multiSnapshot) {
      Array.from(selectedIds).forEach(clearMultiForId)
    }
    clearSelection()
    setMultiSnapshot(null)
    setShowMultiCheckboxes(false)
  }

  function summarizeStagedChanges(): string[] {
    const added: { [type: string]: number } = {}
    const removed: { [type: string]: number } = {}
    const currentStaged = withoutUndefinedActions(staged)

    Object.entries(currentStaged).forEach(([id, action]) => {
      if (!action) return
      const type = action.type
      if (type === 'UNSET') {
        const prevType = override[id]?.type
        if (prevType && prevType !== 'UNSET') {
          removed[prevType] = (removed[prevType] || 0) + 1
        }
      } else {
        added[type] = (added[type] || 0) + 1
      }
    })

    const parts: string[] = []
    const addedParts = Object.entries(added).map(([t, c]) => `${c} ${ActionNames[t] ?? t}`)
    const removedParts = Object.entries(removed).map(([t, c]) => `${c} ${ActionNames[t] ?? t}`)
    if (addedParts.length) parts.push(`add ${addedParts.join(', ')}`)
    if (removedParts.length) parts.push(`remove ${removedParts.join(', ')}`)

    if (!parts.length) {
      return ['No staged changes to apply.']
    }
    return [`You are about to ${parts.join(', ')}.`]
  }

  async function onApplyClick(): Promise<void> {
    const isMultiActive = !!multiSnapshot || showMultiCheckboxes
    if (isMultiActive) {
      const summary = summarizeStagedChanges()
      setConfirmLines(summary)
      setConfirmOpen(true)
    } else {
      await applyStaged()
    }
  }

  return (
    <div>
      <div className="flex my-md pl-lg items-center justify-between">
        <div>
          {(hasWriteCommonAssortmentPermission() || hasWriteInstallationPermission()) && (
            <StagedModifications numberOfStaged={getNumberOfStaged()} onClear={resetStaged} onApply={onApplyClick} />
          )}
        </div>
      </div>
      <div className="flex items-center pl-lg py-sm">
        <div className="flex items-center space-x-2">
          <div className="inline-flex items-center">
            <label className="flex items-center">
              <span>Bottle Group:</span>
              <input
                className="ml-1"
                type="text"
                style={{ width: '60%' }}
                data-testid="search-bottle-group"
                onChange={(e) => {
                  setFilterGroup(e.target.value.toLocaleLowerCase())
                  setCurrentPage(1)
                }}
              />
            </label>
            <span
              className="font-bold cursor-pointer"
              title={
                'You can search for bottle, refund and super groups explicitly like this: `super 12`, `refund 18`, `bottle 122`'
              }
            >
              ?
            </span>
          </div>
          <div className="inline-flex items-center ml-1">
            <label>
              <span>Name:</span>
              <input
                type="text"
                className="ml-1"
                style={{ width: '60%' }}
                data-testid="search-name"
                onChange={(e) => {
                  setFilterName(e.target.value.toLocaleLowerCase())
                  setCurrentPage(1)
                }}
              />
            </label>
          </div>
        </div>
        <div className="ml-auto">
          {multiSnapshot ? (
            <div className="border-4 p-1 " style={{ borderColor: Colors.LIGHT_BLUE, minWidth: 250 }}>
              <span className="font-bold" style={{ color: Colors.LIGHT_BLUE }}>
                Multiselect mode active
              </span>
              <div className="mb-2">
                <span className="font-bold">Multiselect action: </span>
                <EditableActions onClick={openMultiDialog}>
                  {multiSnapshot.returnAction && (
                    <p className="text-xs opacity-70">Return: {multiSnapshot.returnAction.type}</p>
                  )}
                  {multiSnapshot.disabledAction && (
                    <p className="text-xs opacity-70">Disable: {multiSnapshot.disabledAction.type}</p>
                  )}
                </EditableActions>
              </div>
              <button className="btn btn-sm mt-2 w-full" onClick={resetMulti} type="button">
                Cancel and exit multiselect mode
              </button>
            </div>
          ) : (
            hasWriteInstallationPermission() && (
              <div className="inline-flex items-center">
                <span className="mr-1">Start multiselect mode:</span>
                <EditableActions onClick={openMultiDialog} />
              </div>
            )
          )}
        </div>
      </div>
      <div className="flex items-center pl-lg py-sm">
        <div className="inline-flex items-center">
          <label className="flex items-center">
            <span>Show Details:</span>
            <input
              type="checkbox"
              className="checkbox align-middle ml-sm"
              checked={showDetailed}
              onChange={(e) => setShowDetailed(e.target.checked)}
            />
          </label>
        </div>
      </div>
      <table className="table table-fixed mt-1">
        <thead>
          <tr>
            <SortableHeader name="Bottle Group" currentSort={sorting.currentSort} />
            <SortableHeader name="Name" currentSort={sorting.currentSort} />
            <th>Database</th>
            <th>Disable All</th>
            <SortableHeader name="CommonConfig" currentSort={sorting.currentSort} />
            <SortableHeader name="Override" currentSort={sorting.currentSort} />
            <SortableHeader name="Accepted" currentSort={sorting.currentSort} />
            {showMultiCheckboxes && (
              <th>
                Select all
                <input
                  type="checkbox"
                  className="checkbox align-middle ml-sm"
                  onChange={(e) => selectAllIds(e.target.checked)}
                  checked={selectedIds.size === rows.length && rows.length > 0}
                />
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {getPageData(rows, currentPage, recordsPrPage).map((bottleGroup) => {
            const id = bottleGroup.groupNumber
            return (
              <tr key={id} className="even:bg-grey-light hover:bg-fog">
                <td>{bottleGroup.groupNumber}</td>
                <td>{bottleGroup.name}</td>
                <td>
                  <ReturnValue value={bottleGroup.returnFlag} />
                  {showDetailed && (
                    <>
                      <RefundGroupValue value={bottleGroup.refundGroup} />
                      <SuperGroupValue value={bottleGroup.superGroupNumber} />
                    </>
                  )}
                </td>
                <td>
                  {[disabledAllActionId()]
                    .filter((id) => common[id])
                    .map((id) => (
                      <StagedAction key={id} original={common[id]} staged={staged[id]} />
                    ))}
                </td>
                <td>
                  {[returnActionId(id), disabledActionId(id), refundGroupActionId(id), superGroupActionId(id)]
                    .filter((id) => common[id])
                    .map((id) => (
                      <Action key={id} action={common[id]} />
                    ))}
                </td>
                <td>
                  <EditableActions onClick={() => openDialog(id)}>
                    {[returnActionId(id), disabledActionId(id), refundGroupActionId(id), superGroupActionId(id)]
                      .filter((id) => override[id] || staged[id])
                      .map((id) => (
                        <StagedAction key={id} original={override[id]} staged={staged[id]} />
                      ))}
                  </EditableActions>
                </td>
                <td>
                  <FinalStatus ok={calculateFinalStatus(bottleGroup, common, override || {}, staged)} />
                </td>
                {showMultiCheckboxes && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(id)}
                      onChange={() => toggleSelect(id)}
                      aria-label={`select ${id}`}
                    />
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>

      <PagedTableControls
        currentPage={currentPage}
        numOfItems={rows.length}
        recordsPerPage={recordsPrPage}
        onPageUpdate={setCurrentPage}
        onRecordsPerPageChange={(r: number) => {
          setRecordsPrPage(r)
          setCurrentPage(1)
        }}
        recordsPerPageOptions={paginationOptions}
      />

      {dialogState.show && (
        <Dialog onClose={() => setDialogState({ ...dialogState, show: false })} shouldCloseOnOverlayClick={false}>
          {dialogState.id === MULTI_ID ? (
            <ShapeActionDialogMultiselect
              config={config}
              id={dialogState.id}
              snapshot={
                (dialogState.snapshot as ShapeActionSnapshotMultiselect) ?? {
                  returnAction: undefined,
                  disabledAction: undefined,
                }
              }
              disabled={
                (dialogState.disabled as DisabledShapeActionsMultiselect) ?? {
                  returnAction: false,
                  disabledAction: false,
                }
              }
              onApply={saveToStagedMulti}
            />
          ) : (
            <ShapeActionDialog
              config={config}
              id={dialogState.id}
              snapshot={dialogState.snapshot}
              disabled={dialogState.disabled}
              onApply={saveToStaged}
            />
          )}
        </Dialog>
      )}

      {confirmOpen && (
        <Dialog onClose={() => setConfirmOpen(false)} shouldCloseOnOverlayClick={false}>
          <div className="card">
            <FormLayout.Content>
              <div className="mb-sm font-bold text-lg">Confirm apply</div>
              <div className="mb-md">
                {confirmLines.map((l, i) => (
                  <p key={i}>{l}</p>
                ))}
              </div>
              <div className="flex justify-end space-x-2">
                <button className="btn btn-secondary" type="button" onClick={() => setConfirmOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={async () => {
                    await applyStaged()
                    setConfirmOpen(false)
                  }}
                >
                  Confirm
                </button>
              </div>
            </FormLayout.Content>
          </div>
        </Dialog>
      )}
    </div>
  )

  function getNumberOfStaged() {
    return staged[disabledAllActionId()]
      ? countAffectedRows(Object.keys(withoutUndefinedActions(staged))) + rows.length - 1
      : countAffectedRows(Object.keys(withoutUndefinedActions(staged)))
  }

  function resetStaged() {
    clearSelection()
    resetMulti()
    setStaged({})
  }

  async function applyStaged() {
    try {
      await saveActions('SHAPE', modificationUrl, override, staged)
      clearSelection()
      resetMulti()
      setStaged({})
      refreshModifications()
    } catch (error: unknown) {
      logError(error)
    }
  }

  function saveToStaged(id: string, snapshot: ShapeActionSnapshot) {
    if (id === MULTI_ID) {
      setMultiSnapshot(snapshot)
      setShowMultiCheckboxes(true)
      Array.from(selectedIds).forEach(stageMultiForId)
    } else {
      setStaged({
        ...staged,
        [returnActionId(id)]: stagedIfChanged(snapshot.returnAction, override[returnActionId(id)]),
        [disabledActionId(id)]: stagedIfChanged(snapshot.disabledAction, override[disabledActionId(id)]),
        [refundGroupActionId(id)]: stagedIfChanged(snapshot.refundGroupAction, override[refundGroupActionId(id)]),
        [superGroupActionId(id)]: stagedIfChanged(snapshot.superGroupAction, override[superGroupActionId(id)]),
      })
    }
    setDialogState((ds) => ({ ...ds, show: false }))
  }

  function saveToStagedMulti(id: string, snapshot: ShapeActionSnapshotMultiselect) {
    setMultiSnapshot(snapshot)
    setShowMultiCheckboxes(true)
    Array.from(selectedIds).forEach(stageMultiForId)
    setDialogState((ds) => ({ ...ds, show: false }))
  }

  function openDialog(id: string) {
    setDialogState({
      show: true,
      id: id,
      snapshot: {
        returnAction: staged[returnActionId(id)] ?? override[returnActionId(id)],
        disabledAction: staged[disabledActionId(id)] ?? override[disabledActionId(id)],
        refundGroupAction: staged[refundGroupActionId(id)] ?? override[refundGroupActionId(id)],
        superGroupAction: staged[superGroupActionId(id)] ?? override[superGroupActionId(id)],
      },
      disabled: {
        disabledAction:
          typeof common[disabledActionId(id)]?.allowOverride !== 'undefined' &&
          !common[disabledActionId(id)]?.allowOverride,
        returnAction:
          typeof common[returnActionId(id)]?.allowOverride !== 'undefined' &&
          !common[returnActionId(id)]?.allowOverride,
        refundGroupAction:
          typeof common[refundGroupActionId(id)]?.allowOverride !== 'undefined' &&
          !common[refundGroupActionId(id)]?.allowOverride,
        superGroupAction:
          typeof common[superGroupActionId(id)]?.allowOverride !== 'undefined' &&
          !common[superGroupActionId(id)]?.allowOverride,
      },
    })
  }

  function openMultiDialog() {
    setDialogState({
      show: true,
      id: MULTI_ID,
      snapshot: (multiSnapshot as ShapeActionSnapshotMultiselect) ?? {
        returnAction: undefined,
        disabledAction: undefined,
      },
      disabled: {
        returnAction: false,
        disabledAction: false,
        refundGroupAction: false, // present but unused in multi
        superGroupAction: false, // present but unused in multi
      },
    })
  }
}

const calculateFinalStatus = (
  original: BottleGroup,
  common: { [key: string]: ShapeAction },
  override: { [key: string]: ShapeAction },
  staged: { [key: string]: ShapeAction | undefined },
) => {
  const returned =
    staged[returnActionId(original.groupNumber)] ??
    override[returnActionId(original.groupNumber)] ??
    common[returnActionId(original.groupNumber)]
  const disabled =
    staged[disabledActionId(original.groupNumber)] ??
    override[disabledActionId(original.groupNumber)] ??
    common[disabledActionId(original.groupNumber)]
  const disabledAll = staged[disabledAllActionId()] ?? common[disabledAllActionId()]

  const accepted =
    (original.returnFlag === 0 && returned?.type !== ReturnActionType.SET) ||
    (original.returnFlag === 1 && returned?.type === ReturnActionType.CLEAR)

  const enabled =
    (disabledAll?.type !== DisableAllActionType.SET && disabled?.type !== DisabledActionType.SET) ||
    (disabledAll?.type === DisableAllActionType.SET && disabled?.type === DisabledActionType.CLEAR)

  return accepted && enabled
}

const createShapeActions = (modifications: Modification[]): { [key: string]: ShapeAction } =>
  modifications.reduce(
    (actions, modification) => {
      const actionType = modification.actionType
      if (actionType) {
        if (isReturnAction(actionType)) {
          actions[returnActionId(modification.referenceObjectId)] = {
            type: actionType,
            allowOverride: modification.allowedOverride,
          }
        } else if (isDisabledAction(actionType)) {
          actions[disabledActionId(modification.referenceObjectId)] = {
            type: actionType,
            allowOverride: modification.allowedOverride,
          }
        } else if (isRefundGroupAction(actionType)) {
          actions[refundGroupActionId(modification.referenceObjectId)] = {
            type: actionType,
            value: modification.actionValue,
            allowOverride: modification.allowedOverride,
          }
        } else if (isSuperGroupAction(actionType)) {
          actions[superGroupActionId(modification.referenceObjectId)] = {
            type: actionType,
            value: modification.actionValue,
            allowOverride: modification.allowedOverride,
          }
        } else if (isDisableAllAction(actionType)) {
          actions[disabledAllActionId()] = {
            type: actionType,
            allowOverride: modification.allowedOverride,
          }
        }
      }
      return actions
    },
    {} as { [key: string]: ShapeAction },
  )

const returnActionId = (id: string) => id + '[RETURN]'
const disabledActionId = (id: string) => id + '[DISABLE]'
const disabledAllActionId = () => '[DISABLE_ALL]'
const refundGroupActionId = (id: string) => id + '[REFUND_GROUP]'
const superGroupActionId = (id: string) => id + '[SUPER_GROUP]'
